import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin } from 'lucide-react';
import api from '../services/api';
import { useClientAuth } from '../context/ClientAuthContext';
import { getStoredConversationId, setStoredConversationId } from '../utils/buyerChatSession';
import { clientAuthConfig } from '../utils/clientAuthConfig';
import { chatCardBadgeLabel } from '../utils/buyerChatTurn';
import { provinceLabel as getProvinceLabel } from '../utils/provinceLabel';
import './ClientPortal.css';

function draftCriteriaStorageKey(slug) {
  return `a4a_draft_criteria_${slug}`;
}

function getAgentPhotoSrc(agent) {
  if (!agent?.has_agent_photo || !agent?.id) return null;
  return `/api/auth/agent-photo/${agent.id}`;
}

/** Prefer the live conversation the buyer came from; else agent chat home. */
function resolveChatReturnPath(slug, pathMode, searchParams) {
  if (!slug) return '/';
  const fromParam = searchParams.get('conversation')
    || searchParams.get('conv')
    || null;
  const stored = getStoredConversationId(slug, pathMode);
  const conversationId = fromParam || stored;
  if (conversationId) return `/chat/${slug}/conversation/${conversationId}`;
  return `/chat/${slug}`;
}

export default function ClientAuth() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useClientAuth();

  const initialMode = searchParams.get('mode') === 'login' ? 'login' : 'register';
  const roleFromUrl = searchParams.get('role') === 'seller' ? 'seller' : 'buyer';
  const pathMode = roleFromUrl === 'seller' ? 'seller' : 'buyer';

  const [agent, setAgent] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [forgotNotice, setForgotNotice] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    consent_data_storage: false,
    consent_marketing: false,
  });

  const conversationId = useMemo(() => {
    if (!slug) return null;
    return searchParams.get('conversation')
      || searchParams.get('conv')
      || getStoredConversationId(slug, pathMode)
      || null;
  }, [slug, pathMode, searchParams]);

  const chatReturnPath = resolveChatReturnPath(slug, pathMode, searchParams);
  const agentFirst = agent?.first_name || 'your agent';
  const agentName = agent ? `${agent.first_name} ${agent.last_name}` : agentFirst;
  const agentInitials = agent
    ? `${agent.first_name?.[0] || ''}${agent.last_name?.[0] || ''}`
    : '';
  const aiName = agent?.assistant_name || 'Ava';
  const isSellerFlow = roleFromUrl === 'seller';
  const provinceLabel = getProvinceLabel(agent?.province);
  const serviceAreaLabel = String(agent?.service_area_label || agent?.city || '').trim();

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    let retryTimer = null;
    const load = (retriesLeft) => {
      api.get(`/auth/agent/${slug}`)
        .then((res) => {
          if (!cancelled) setAgent(res.data.agent);
        })
        .catch(() => {
          // A server restart or slow start can fail this once; retry so the header
          // shows the agent's name instead of the "your agent" fallback.
          if (!cancelled && retriesLeft > 0) retryTimer = setTimeout(() => load(retriesLeft - 1), 2000);
        });
    };
    load(2);
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [slug]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setForgotNotice(false);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('mode', next);
    setSearchParams(nextParams, { replace: true });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setForgotNotice(false);
    setSubmitting(true);

    let draftCriteria;
    try {
      const raw = slug ? localStorage.getItem(draftCriteriaStorageKey(slug)) : null;
      if (raw) draftCriteria = JSON.parse(raw);
    } catch { /* ignore */ }

    const payload = {
      ...form,
      agent_slug: slug,
      conversation_id: conversationId || undefined,
      draft_criteria: draftCriteria || undefined,
      client_role: mode === 'register' ? roleFromUrl : undefined,
    };

    try {
      const endpoint = mode === 'login' ? '/clients/login' : '/clients/register';
      if (mode === 'register' && !form.consent_data_storage) {
        setError(
          isSellerFlow
            ? 'Please agree to store your property details and conversation history.'
            : 'Please agree to store your search preferences and conversation history.'
        );
        setSubmitting(false);
        return;
      }

      const res = await api.post(endpoint, payload);
      setSession(slug, res.data.token, res.data.client, {
        showResumeRecap: mode === 'login' || (mode === 'register' && roleFromUrl === 'seller'),
      });
      if (slug) localStorage.removeItem(draftCriteriaStorageKey(slug));

      // Return to the live chat they came from — only if the backend actually
      // confirmed the conversation is linked to this account. On login, a
      // stray/foreign conversation_id (e.g. left over from a different
      // account's session) won't come back linked, and navigating there
      // anyway would just get the client 403'd out with no working chat.
      // Buyer login: "pick up where you left off" means their last chat with
      // Ava. Use the conversation the backend just linked, else ask for the
      // account's latest one (never a stray id left in this browser).
      if (mode === 'login' && !isSellerFlow) {
        let resumeId = conversationId && res.data.conversation_id === conversationId
          ? conversationId
          : null;
        if (!resumeId) {
          try {
            const latest = await api.get('/clients/me/conversations/latest', clientAuthConfig(slug));
            resumeId = latest.data?.conversation?.conversation_id || null;
          } catch { /* no linked conversation — fall through to the workspace */ }
        }
        if (resumeId) {
          setStoredConversationId(slug, resumeId, 'buyer');
          navigate(`/chat/${slug}/conversation/${resumeId}`, { replace: true });
          return;
        }
      }

      if (conversationId && res.data.conversation_id === conversationId) {
        navigate(`/chat/${slug}/conversation/${conversationId}`, { replace: true });
        return;
      }

      // The portal's Overview page adapts to whatever role(s) the client has —
      // no need to branch by role here anymore.
      navigate(`/client/${slug}`, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="client-page client-page--auth client-page--a4a">
      <div className="client-page__orb client-page__orb--tr" aria-hidden />
      <div className="client-page__orb client-page__orb--bl" aria-hidden />

      <div className="client-page__auth-shell">
        <header className="client-auth-strip">
          <div className="client-auth-strip__identity">
            <div className="client-auth-strip__avatar">
              {getAgentPhotoSrc(agent) ? (
                <img
                  src={getAgentPhotoSrc(agent)}
                  alt=""
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                agentInitials || 'SM'
              )}
            </div>
            <div>
              <div className="client-auth-strip__name">{agentName}</div>
              <div className="client-auth-strip__tagline">
                {agent?.brand_tagline || `Licensed REALTOR® · ${agent?.brokerage || 'Your local agent'}`}
              </div>
              {(agent?.city || serviceAreaLabel) && (
                <div className="client-auth-strip__meta">
                  <span>
                    <MapPin size={11} aria-hidden />
                    {' '}
                    {serviceAreaLabel
                      ? chatCardBadgeLabel(serviceAreaLabel)
                      : [agent.city, provinceLabel].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="client-page__card client-page__card--auth">
          <Link to={chatReturnPath} className="client-page__back">
            <ArrowLeft size={16} aria-hidden /> Back to chat
          </Link>

          <div className="client-page__auth-single">
            <header className="client-page__auth-heading">
              <h1>
                {mode === 'login'
                  ? 'Sign in'
                  : isSellerFlow
                    ? `Save your consult with ${agentFirst}`
                    : 'Create your free account'}
              </h1>
              <p>
                {mode === 'login'
                  ? (isSellerFlow
                    ? `Pick up right where you left off — your property details and conversation with ${aiName}.`
                    : `Pick up right where you left off — your saved search and conversation with ${aiName}.`)
                  : isSellerFlow
                    ? `Keep your property details and consultation notes from ${aiName} so ${agentFirst} can follow up.`
                    : `Save what you've told ${aiName} — budget, areas, and this chat — so nothing gets lost.`}
              </p>
            </header>

            <form className="client-form" onSubmit={handleSubmit}>
              {mode === 'register' && (
                <>
                  <div className="client-form__row">
                    <label>
                      First name
                      <input name="first_name" value={form.first_name} onChange={handleChange} required autoComplete="given-name" />
                    </label>
                    <label>
                      Last name
                      <input name="last_name" value={form.last_name} onChange={handleChange} required autoComplete="family-name" />
                    </label>
                  </div>
                  <label>
                    Phone (optional — helps {agentFirst} reach you)
                    <input name="phone" type="tel" value={form.phone} onChange={handleChange} autoComplete="tel" />
                  </label>
                </>
              )}

              <label>
                Email
                <input name="email" type="email" value={form.email} onChange={handleChange} required autoComplete="email" />
              </label>

              <label>
                Password
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
              </label>

              {mode === 'login' && (
                <div className="client-form__forgot-row">
                  <button
                    type="button"
                    className="client-form__forgot"
                    onClick={() => setForgotNotice(true)}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {forgotNotice && (
                <p className="client-form__forgot-notice" role="status">
                  Password reset isn&apos;t available in the app yet. Call or text {agentFirst}
                  {agent?.phone ? ` at ${agent.phone}` : ''} and they&apos;ll help you get back in —
                  or continue your chat with {aiName} and ask them to follow up.
                </p>
              )}

              {mode === 'register' && (
                <div className="client-form__checks">
                  <label className="client-form__check">
                    <input
                      type="checkbox"
                      name="consent_data_storage"
                      checked={form.consent_data_storage}
                      onChange={handleChange}
                    />
                    {isSellerFlow
                      ? `I agree to store my property details, consultation notes, and conversation history with ${agentFirst}.`
                      : `I agree to store my search preferences and conversation history with ${agentFirst}.`}
                  </label>
                  {!isSellerFlow && (
                    <label className="client-form__check">
                      <input
                        type="checkbox"
                        name="consent_marketing"
                        checked={form.consent_marketing}
                        onChange={handleChange}
                      />
                      Email me when new properties match my criteria (optional).
                    </label>
                  )}
                  <p className="client-form__legal">
                    See our{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>.
                  </p>
                </div>
              )}

              {error && <p className="client-form__error">{error}</p>}

              <button type="submit" className="client-form__submit" disabled={submitting}>
                {submitting
                  ? 'Please wait…'
                  : mode === 'login'
                    ? 'Sign in'
                    : (isSellerFlow ? 'Save my consult' : 'Create free account')}
              </button>
            </form>

            <p className="client-page__auth-switch">
              {mode === 'login' ? (
                <>
                  New here?{' '}
                  <button type="button" className="client-page__auth-switch-btn" onClick={() => switchMode('register')}>
                    Create your free account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button type="button" className="client-page__auth-switch-btn" onClick={() => switchMode('login')}>
                    Sign in
                  </button>
                </>
              )}
            </p>

            {conversationId && (
              <p className="client-page__hint">
                {mode === 'register'
                  ? `Your current chat with ${aiName} will be linked to this account.`
                  : `You'll return to your chat with ${aiName} after signing in.`}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
